import React, { useMemo } from "react";

import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import { IconButton, Stack, Typography } from "@mui/material";
import { WorkspaceFolderCloseIcon } from "assets/material-custom-icons";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { FolderActions } from "components/Deal-workspace/Menus/FolderActions";
import { IFolder } from "store/types";

import { useClientWorkspaceCtx } from "../context/ClientWorkspace-context";
import { toDealFolderShape, useClientFolderActions } from "../Hooks/use-folder-actions-hook";
import { useFolderHook } from "../Hooks/use-folder-hook";
import { useFolderDragDrop } from "../Hooks/use-workspace-dnd";

export const ROW_HEIGHT = 36;

interface Props {
  folder: IFolder;
  depth: number;
  folderList: IFolder[];
}

const INDENT_PX = 25;
const chevronColor = "#0A3142";
const iconColor = "#0A3142";
const labelColor = "#20293C";

export const Folder: React.FC<Props> = React.memo(({ folder, depth, folderList }) => {
  const { isFolderOpen, onFolderArrowClick, currentFolder, onFolderClick, getChildFolders, refetchFolderMetadata } =
    useClientWorkspaceCtx();

  const childFolders = getChildFolders(folder.id, folderList);
  const isOpen = isFolderOpen(folder.id);
  const hasChildren = Boolean(childFolders.length);
  const isSelected = currentFolder?.id === folder.id;

  const folderIcon = useMemo(
    () => <WorkspaceFolderCloseIcon sx={{ fill: iconColor, fontSize: "20px" }} />,
    []
  );

  const { onDropFiles } = useFolderHook({ folder, refetchHandler: refetchFolderMetadata });
  const { isDragOver, onDragOver, onDragLeave, onDrop } = useFolderDragDrop({
    folder,
    onDropFiles
  });

  const onFolderArrowClickHandler = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasChildren) {
      onFolderArrowClick(folder.id);
    }
  };

  const handleTitleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onFolderClick(folder.id);
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const folderPadding = useMemo(() => {
    if (depth) return `${depth * INDENT_PX + 10}px`;
    return "5px";
  }, [depth]);

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap="5px"
      onContextMenu={handleContextMenu}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      sx={{
        width: "100%",
        height: ROW_HEIGHT,
        padding: "0 5px",
        borderRadius: "20px",
        paddingLeft: folderPadding,
        backgroundColor: isDragOver ? "#C8DCF0" : isSelected ? "#DBE8F5" : "transparent",
        outline: isDragOver ? "2px dashed #5B9BD5" : "none",
        outlineOffset: "-2px",
        "&:hover": {
          backgroundColor: isDragOver ? "#C8DCF0" : "#ECF1F8"
        },
        "& .folder-menu-trigger": {
          opacity: 0
        },
        "&:hover .folder-menu-trigger": {
          opacity: 0.7
        },
        "& .folder-menu-trigger:hover": {
          opacity: 1
        }
      }}
    >
      {hasChildren ? (
        <IconButton onClick={onFolderArrowClickHandler} sx={{ cursor: "pointer" }}>
          <ExpandMoreOutlinedIcon
            sx={{
              fill: chevronColor,
              fontSize: 20,
              transform: hasChildren && isOpen ? "rotate(0deg)" : "rotate(-90deg)",
              transition: "transform 0.2s ease"
            }}
          />
        </IconButton>
      ) : (
        <Stack sx={{ padding: "16px" }} />
      )}
      <Stack
        direction="row"
        alignItems="center"
        gap="5px"
        onClick={handleTitleClick}
        sx={{
          flex: 1,
          minWidth: 0,
          cursor: "pointer"
        }}
      >
        {folderIcon}
        <MaterialTooltip title={folder.title}>
          <Typography
            noWrap
            sx={{
              color: labelColor,
              fontWeight: 400,
              fontSize: "16px"
            }}
          >
            {folder.title}
          </Typography>
        </MaterialTooltip>
      </Stack>
      <Stack direction="row" alignItems="center" sx={{ minWidth: 32 }}>
        <FolderActions
          folder={toDealFolderShape(folder)}
          refetchHandler={refetchFolderMetadata}
          useHook={useClientFolderActions}
        />
      </Stack>
    </Stack>
  );
});
